import React, { useMemo } from 'react';
import { sortBy } from 'lodash';

import { Site, getSiteTags, TagDefinition } from '../../../models';
import {
  useSharedStyles,
  ExpandableListGroup,
  TagList,
  FilterableTagList,
  SelectedTagsList,
} from './common';

export interface TagTreeProps {
  site: Site;
  onTagSelect: (tagId: string) => void;
  clearAll?: () => void;
  selectedTags: string[];
  tagFilter?: (tag: TagDefinition) => boolean;
  height?: number | string;
  style?: React.CSSProperties;
  disabled?: (tag: TagDefinition) => boolean;
  styleContent?: React.CSSProperties;
  accordionContainerHeight?: { xs: string; sm: string };
  hideSelectedWhenEmpty?: boolean;
  tabs?: React.ReactNode;
}

export const TagTree: React.FC<TagTreeProps> = (props) => {
  const {
    site,
    selectedTags,
    disabled,
    tagFilter,
    hideSelectedWhenEmpty = true,
    clearAll,
    onTagSelect,
    tabs,
  } = props;

  const sharedClasses = useSharedStyles({
    height: props.height,
  });

  const allTags = useMemo(() => {
    if (tagFilter) {
      return getSiteTags(site).filter(tagFilter);
    } else {
      return getSiteTags(site);
    }
  }, [site, tagFilter]);

  const filterTags = (tag: TagDefinition, filterText: string): boolean => {
    const textFilter = tag.label.toLowerCase().includes(filterText.toLowerCase());
    const topFilter = props.tagFilter ? props.tagFilter(tag) : true;
    return textFilter && topFilter;
  };

  const renderAvailableTags = (filteredTags: TagDefinition[]) => {
    const nodes = site.nodes.filter((node) => node.equipment.length > 0);

    return (
      <>
        {nodes.length === 1 &&
          sortBy(nodes[0].equipment, (e) => e.label).map((equipment) => {
            const tags = equipment.tags.filter((tag) => filteredTags.includes(tag));
            return tags.length > 0 ? (
              <ExpandableListGroup
                key={equipment.id.toString()}
                id={equipment.id.toString()}
                label={equipment.label}
              >
                <TagList
                  tags={tags}
                  onToggle={props.onTagSelect}
                  selectedTags={selectedTags}
                  disabled={disabled}
                />
              </ExpandableListGroup>
            ) : null;
          })}

        {nodes.length > 1 ? (
          nodes.map((node) => (
            <ExpandableListGroup key={node.id.toString()} id={node.id.toString()} label={node.name}>
              {sortBy(node.equipment, (e) => e.label).map((equipment) => {
                const tags = equipment.tags.filter((tag) => filteredTags.includes(tag));
                return tags.length > 0 ? (
                  <ExpandableListGroup
                    key={equipment.id.toString()}
                    id={equipment.id.toString()}
                    label={equipment.label}
                  >
                    <TagList
                      tags={tags}
                      onToggle={props.onTagSelect}
                      selectedTags={selectedTags}
                      disabled={disabled}
                    />
                  </ExpandableListGroup>
                ) : null;
              })}
              <TagList
                tags={node.tags.filter((tag) => filteredTags.includes(tag))}
                onToggle={props.onTagSelect}
                selectedTags={selectedTags}
                disabled={disabled}
              />
            </ExpandableListGroup>
          ))
        ) : (
          <TagList
            tags={site.nodes[0].tags.filter((tag) => filteredTags.includes(tag))}
            onToggle={props.onTagSelect}
            selectedTags={selectedTags}
            disabled={disabled}
          />
        )}
        <TagList
          tags={site.tags.filter((tag) => filteredTags.includes(tag))}
          onToggle={props.onTagSelect}
          selectedTags={selectedTags}
          disabled={disabled}
        />
      </>
    );
  };

  return (
    <div className={sharedClasses.mainContainer}>
      <div className={sharedClasses.container}>
        <SelectedTagsList
          tags={allTags}
          selectedTags={selectedTags}
          onTagSelect={onTagSelect}
          clearAll={clearAll}
          hideWhenEmpty={hideSelectedWhenEmpty}
        />
        <FilterableTagList
          tabs={tabs}
          tags={allTags}
          renderTags={renderAvailableTags}
          filterCallback={filterTags}
        />
      </div>
    </div>
  );
};
